import React, { useEffect, useRef } from 'react';
import './Article.scss';
import { gsap } from './gsap';
import { createPopper } from '@popperjs/core';
import Hero from './Components/Hero/Hero';

import Header from './Components/common-header/Header';
import Footer from './Components/common-footer/Footer';
import InlinePhoto from './Components/Photos/InlinePhoto';
import FullWidthPhoto from './Components/Photos/FullWidthPhoto';
import FullWidthVideo from './Components/Photos/FullWidthVideo';
import Chapter1 from './Components/Chapters/Chapter1';
import Chapter2 from './Components/Chapters/Chapter2';
import Chapter3 from './Components/Chapters/Chapter3';
import Chapter4 from './Components/Chapters/Chapter4';
import Chapter5 from './Components/Chapters/Chapter5';
import Chapter6 from './Components/Chapters/Chapter6';

import PullQuote from './Components/PullQuote/PullQuote';
import PullQuoteWImg from './Components/PullQuote/PullQuoteWImg';
// import InlineVideo from './Components/Photos/InlineVideo';
// import FullWidthVideo from './Components/Photos/FullWidthVideo';
import contents from './contents';
import RelatedContents from './Components/RelatedContents/RelatedContents';
import Reference from './Components/Reference/Reference';
import CtaButton from './Components/CtaButton/CtaButton';

//for fullWidth Photos
import fullbleedPhoto1 from './Assets/fullbleedPhoto1.jpg';
import fullbleedPhoto1M from './Assets/fullbleedPhoto1M.jpg';

//pullQuotePhotos
import pqImg1 from './Assets/pqImg1.jpg';
import pqImg2 from './Assets/pqImg2.jpg';

function Article({ heroBackgroundType, inlinePhotoSize, logoPlacement }) {
  //below states are for the audio player placement. See README for detail.

  //create refs for photos here if you want to add scale animation on scroll
  const inline1 = useRef();
  const inline2 = useRef();
  const inline3 = useRef();
  const inline4 = useRef();
  const inline5 = useRef();
  const inline6 = useRef();
  const inline7 = useRef();
  const allPhotosRef = useRef();
  allPhotosRef.current = [inline1, inline2, inline3, inline4, inline5, inline6, inline7]; // put all photo refs in this array

  const screenSizeRef = useRef(); //

  const photoSize = inlinePhotoSize !== undefined ? inlinePhotoSize : contents.inlinePhotos.size;

  //creates/shows tooltip element when mouse enters
  const handleMouseEnterTooltip = (e, currentRef) => {
    const content = currentRef.current;
    createPopper(e.target, content, {
      placement: 'bottom',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 5]
          }
        }
      ]
    });
    content.style.visibility = 'visible';
  };

  //hides tooltip element when mouse leaves
  const handleMouseLeaveTooltip = (currentRef) => {
    currentRef.current.style.visibility = 'hidden';
  };

  useEffect(() => {
    allPhotosRef.current.forEach((photo) => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: photo.current,
          start: '20% bottom',
          end: 'top center',
          scrub: 1
        }
      });
      tl.to(photo.current, {
        width: '100%',
        height: '100%',
        duration: 3
      });
    });
  }, []);

  return (
    <div className="article">
      <div className="height" ref={screenSizeRef} />
      <Header clientName={contents.header} />
      <Hero
        handleMouseEnterTooltip={handleMouseEnterTooltip}
        handleMouseLeaveTooltip={handleMouseLeaveTooltip}
        heroBackgroundType={heroBackgroundType}
        logoPlacement={logoPlacement}
      />
      <Chapter1 />
      <InlinePhoto
        photoSize={photoSize}
        photoContents={contents.inlinePhotos.contents[0]}
        photoRef={inline1}
      />

      {/* <InlineVideo
        vidLink={
          'https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/graphicOrbs.mp4'
        }
        vidLinkM={
          'https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/graphicOrbsM.mp4'
        }
        caption={
          'A forthcoming high-tech development in quaint Castle Rock, Co., Miller’s Landing boasts frictionless connectivity while retaining the area’s natural charm. Restaurant reservations, work solutions, and even hospital visits can be managed from a smartphone thanks to Intel’s networking capabilities. '
        }
      /> */}

      <Chapter2 />
      <PullQuoteWImg
        quote={
          'I know some high school students who can [create deepfakes] without any problem, without any background research.'
        }
        author={'Dr. Umur Aybars Ciftci,'}
        descrip={'Co-Creator of FakeCatcher'}
        photo={pqImg1}
      />
      <Chapter3 />
      <PullQuoteWImg
        quote={
          'When deepfakes appear, currently there is no mechanism to automatically mark them as fake or not.'
        }
        author={'Dr. Ilke Demir,'}
        descrip={'Intel Labs’ Senior Staff Research Scientist'}
        photo={pqImg2}
      />
      <FullWidthPhoto
        photo={fullbleedPhoto1}
        photoM={fullbleedPhoto1M}
        alt={'containers'}
        caption={
          'The detector platform runs FakeCatcher on servers with 3rd Gen Intel® Xeon® Scalable processors, capable of operating 72 different (and simultaneous) detection streams. Each detection stream will tell you the percentage of confidence on a different real or fake video.'
        }
      />
      <PullQuote
        quote={
          'Deepfakes enable preserving the microexpressions, gestures, and emotions of the subject [while] masking their identity with a non-existing person to protect them.'
        }
        author={'Dr. Ilke Demir,'}
        source={'Intel Labs'}
      />

      <Chapter4 />

      <FullWidthVideo
        vidLink="https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/deepfakesEyes.mp4"
        vidLinkM="https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/deepfakesEyesM.mp4"
        caption="The eyes have it: 3D gaze information can be analyzed to determine whether a video subject is truly human. For instance, gaze consistency is one optical indicator that can be assessed by detection technology."
      />
      <Chapter5 />
      <InlinePhoto
        photoSize={photoSize}
        photoContents={contents.inlinePhotos.contents[1]}
        photoRef={inline2}
      />

      <Chapter6 />

      <FullWidthVideo
        vidLink="https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/deepfakesBottomVid.mp4"
        vidLinkM="https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/deepfakesBottomVidM.mp4"
        caption="One terrible consequence of deceptive deepfakes is their ability to erode trust in legitimate media. Manipulated videos of newscasters and politicians can cause confusion—something we’ve already seen in the real world. Current detection apps can take hours to analyze video content, but Intel’s real-time detection platform, using FakeCatcher, takes just milliseconds, so news media can determine authenticity in real-time."
      />
      <Reference />
      <CtaButton
        handleMouseEnterTooltip={handleMouseEnterTooltip}
        handleMouseLeaveTooltip={handleMouseLeaveTooltip}
      />
      {/* <div className="article__divider" /> */}

      <RelatedContents />
      <Footer />
    </div>
  );
}

export default Article;
